import { Box, Text, ScrollArea } from '@mantine/core';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWorkflowChrome } from '../../../../../components/Layout/WorkflowChromeContext';

import { NoJurisdictions } from './components/NoJurisdictions';

import { ESMAJurisdiction } from './components/ESMA';
import {
  useDeleteJurisdiction,
  useGetCustomMasterFunds,
  useGetEuropeCountries
} from '../../../../../services/jurisdiction.service';
import { v4 } from 'uuid';
import toast from 'react-hot-toast';
import AIFMustBeUniqueWarning from './components/Warnings/UniqueWarnings';
import { countryRules } from './utils';
import agentStore from '../../../../../store/agent';
import { WEB_PAGE_STATE_ENUMS } from '../../../../../components/ChatWidget/constants';
import { REPORT_NAMINGS } from '../../../../../consts/reportTypes';
import { JURISDICTION_MASTER_STATUS_KEYS } from '../../../../../consts/jurisdictions';
import { cloneDeep } from 'lodash';
import {
  createNewForm,
  mapCustomFunds,
  mapFundsFromJuris,
  mapFundsFromNew,
  mergeCustomFundsOnly
} from './helpers';
import { extractApiError } from '../../../../../utils';

export const FundsJurisdictions = ({
  open,
  funds,
  onClose,
  periodId,
  workflowId,
  onSubmit,
  isMutating,
  isLoadingFunds,
  reportType,
  jurisdictions,
  refetchJurisdictions,
  canManageWorkflow = true,
  isLockedPeriod = false
}) => {
  const allowed = canManageWorkflow && !isLockedPeriod;
  const deleteJurisdiction = useDeleteJurisdiction();
  const { setToolbarSlots, setLabels } = useWorkflowChrome();

  const [form, setForms] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [forceExpand, setForceExpand] = useState();
  const [deletingJurisdictionId, setDeletingJurisdictionId] = useState(null);
  const [mutatedCustomMasterFunds, setMutatedCustomMasterFunds] = useState([]);

  const { data: customMasterFunds, refetch: refetchCustomMasterFunds } = useGetCustomMasterFunds({
    periodId,
    workflowId,
    queryParams: {
      enabled: !!open
    }
  });

  useEffect(() => {
    setMutatedCustomMasterFunds(
      customMasterFunds?.map(fund => ({
        ...fund,
        name: fund.fund_name,
        isMasterFund: true
      })) || []
    );
  }, [customMasterFunds]);

  const aifUniqueIssues = useMemo(() => {
    const allFundCodes = new Set();

    const fundCodes = new Set();

    for (const item of form) {
      for (const fund of item.funds) {
        if (fund.code !== '' && fund.code !== null && allFundCodes.has(fund.code)) {
          fundCodes.add(`${item.generatedId}-${fund.key}`);
        } else {
          allFundCodes.add(fund.code);
        }
      }
    }

    return [...fundCodes];
  }, [form]);

  const aifmUniqueIssues = useMemo(() => {
    const aifmCount = form.reduce((acc, item) => {
      if (item.aifm) {
        acc[item.aifm] = (acc[item.aifm] || 0) + 1;
      }
      return acc;
    }, {});

    const nonUniqueItems = form
      .filter(item => item.aifm && aifmCount[item.aifm] > 1)
      .map(el => el.generatedId);

    return nonUniqueItems;
  }, [JSON.stringify(form)]);

  const emptyAIFMCodes = useMemo(() => {
    return form.filter(el => !el.aifm).map(el => el.generatedId);
  }, [form]);

  const emptyJurisdictionCountry = useMemo(() => {
    return form?.some(el => !el.country);
  }, [form]);

  const emptyNationalCodes = useMemo(() => {
    const funds = [];

    form.forEach(el => {
      el.funds.forEach(fnd => {
        if (!fnd.code && fnd.isIncluded && !fnd.isCustomMasterFund)
          funds.push(`${el.generatedId}-${fnd.key}`);
      });
    });

    return funds;
  }, [JSON.stringify(form)]);

  const hasOneFormAtLeast = useMemo(() => {
    return form.length > 0;
  }, [form]);

  const disabledSubmitButton =
    aifUniqueIssues.length > 0 ||
    aifmUniqueIssues.length > 0 ||
    emptyAIFMCodes.length > 0 ||
    emptyNationalCodes.length > 0 ||
    emptyJurisdictionCountry ||
    !hasOneFormAtLeast ||
    isMutating;

  const isFeederHasNoMasterFund = useMemo(() => {
    if (!form) return false;

    return form?.some(juris =>
      juris?.funds?.some(
        fnd =>
          fnd.masterFeederStatus === JURISDICTION_MASTER_STATUS_KEYS.FEEDER &&
          fnd.masterFundIds?.length === 0
      )
    );
  }, [JSON.stringify(form)]);

  const disableSubmitMessage = useMemo(() => {
    if (aifUniqueIssues.length > 0 || aifmUniqueIssues.length > 0) {
      return 'Please resolve all unique code issues before submitting.';
    }
    if (emptyAIFMCodes.length > 0) {
      return 'Please fill in all AIFM codes before submitting.';
    }
    if (emptyJurisdictionCountry) {
      return 'Please select a country for all jurisdictions before submitting.';
    }
    if (emptyNationalCodes.length > 0) {
      return 'Please fill in all fund codes before submitting.';
    }
    if (!hasOneFormAtLeast) {
      return 'Please add at least one jurisdiction before submitting.';
    }
    if (isMutating) {
      return 'Your changes are being saved. Please wait...';
    }

    if (isFeederHasNoMasterFund) {
      return 'Please select a Master Fund for all feeder funds before submitting.';
    }

    return '';
  }, [
    aifUniqueIssues,
    aifmUniqueIssues,
    emptyAIFMCodes,
    emptyJurisdictionCountry,
    emptyNationalCodes,
    hasOneFormAtLeast,
    isMutating,
    isFeederHasNoMasterFund
  ]);

  useEffect(() => {
    if (!open) {
      setForms([]);
      return;
    }

    setForms(prev => {
      let updated = cloneDeep(prev || []);

      if (!jurisdictions?.length) {
        return updated.map(form => ({
          ...form,
          funds: mergeCustomFundsOnly(form.funds || [], customMasterFunds)
        }));
      }

      const customMasterIds = (customMasterFunds || []).map(f => f.id);

      jurisdictions.forEach(juris => {
        const existing = updated.find(f => f.id === juris.id);

        const jurisNational = juris.national_codes.filter(
          nat => !customMasterIds.includes(nat.fund_id)
        );

        const fundsFromJuris = mapFundsFromJuris(jurisNational, existing?.funds);
        const fundsFromNew = mapFundsFromNew(funds, juris.national_codes, existing?.funds);
        const customFunds =
          customMasterFunds?.length > 0
            ? mapCustomFunds(customMasterFunds, juris, existing?.funds)
            : [];

        const mergedFunds = [...customFunds, ...fundsFromJuris, ...fundsFromNew];

        if (existing) {
          existing.funds = mergedFunds;
        } else {
          updated.push(createNewForm(juris, mergedFunds));
        }
      });

      // for new jurisdiction forms, add any custom master funds that are missing
      if (prev?.length > 0 && prev.some(el => !el.id)) {
        updated = updated.map(form => {
          if (!form.id) {
            return {
              ...form,
              funds: mergeCustomFundsOnly(form.funds || [], customMasterFunds)
            };
          }
          return form;
        });
      }

      return updated;
    });
  }, [jurisdictions, funds, customMasterFunds, open]);

  const { data: countries = [] } = useGetEuropeCountries({
    queryParams: {
      enabled: !!open,
      staleTime: 3000,
      cacheTime: 5000
    }
  });

  const handleAddJurisdiction = () => {
    const newGeneratedId = v4();
    const fundCurrencyMap = {};
    const statusMap = {};
    const masterFundIds = {};

    if (isLoadingFunds) {
      toast.error('Funds are still loading, please wait a moment before adding a jurisdiction.');
      return;
    }

    if (Array.isArray(form)) {
      form.forEach(obj => {
        obj?.funds?.forEach(fund => {
          fundCurrencyMap[fund.key] = fund.baseCurrency;
          statusMap[fund.key] = fund.masterFeederStatus;
          masterFundIds[fund.key] = fund.masterFundIds;
        });
      });
    }

    const fundsWithBaseCurrencies =
      Array.isArray(funds) && funds?.length
        ? funds.map(fund => {
            return {
              key: fund.id,
              name: fund.name,
              code: null,
              isIncluded: true,
              isLastReporting: false,
              masterFundIds: masterFundIds[fund.id] || [],
              masterFeederStatus: statusMap[fund.id] || null,
              baseCurrency: fundCurrencyMap[fund.id]
            };
          })
        : [];

    const customFunds =
      Array.isArray(customMasterFunds) && customMasterFunds?.length
        ? customMasterFunds.map(fund => {
            return {
              key: fund?.fund_id || fund.id,
              name: fund.fund_name,
              code: null,
              isIncluded: true,
              isLastReporting: false,
              isCustomMasterFund: true,
              masterFundIds: masterFundIds[fund?.fund_id || fund.id] || [],
              masterFeederStatus: statusMap[fund?.fund_id || fund.id] || null,
              baseCurrency: fundCurrencyMap[fund?.fund_id || fund.id]
            };
          })
        : [];

    setForms(prev => [
      ...(prev || []),
      {
        generatedId: newGeneratedId,
        aifm: null,
        country: null,
        funds: [...(customFunds || []), ...(fundsWithBaseCurrencies || [])]
      }
    ]);
    setHasChanges(true);
    setTimeout(() => {
      document.getElementById('jurisdiction-forms__end').scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      });
    }, 100);
  };

  const handleChangeAIFMCode = useCallback(
    (value, index) => {
      const mutatedForm = [...form];
      const selectedElForm = mutatedForm[index];

      selectedElForm.aifm = value;

      mutatedForm[index] = selectedElForm;

      setHasChanges(true);
      setForms(mutatedForm);
    },
    [form]
  );

  const handleChangeAIFCode = useCallback(
    (value, fundId, index) => {
      const mutatedForm = [...form];

      const selectedElForm = mutatedForm[index];
      const fundIndex = selectedElForm?.funds?.findIndex(fund => fund.key === fundId);

      selectedElForm.funds[fundIndex].code = value;
      mutatedForm[index] = selectedElForm;

      setHasChanges(true);
      setForms(mutatedForm);
    },
    [form]
  );

  const handleToggleIncluded = useCallback(
    (value, fundId, index) => {
      const mutatedForm = [...form];

      const selectedElForm = mutatedForm[index];
      const fundIndex = selectedElForm?.funds?.findIndex(fund => fund.key === fundId);

      selectedElForm.funds[fundIndex].isIncluded = value;
      mutatedForm[index] = selectedElForm;

      setHasChanges(true);
      setForms(mutatedForm);
    },
    [form]
  );

  const handleBaseCurrencyChange = (value, fundKey, jurIdx) => {
    try {
      const mutatedForm = form.map(jurisdiction => {
        let mutatedFunds = jurisdiction?.funds.map(fund => {
          if (fund?.key === fundKey)
            return {
              ...fund,
              baseCurrency: value
            };

          return fund;
        });

        return { ...jurisdiction, funds: mutatedFunds };
      });

      setHasChanges(true);
      setForms(mutatedForm);
    } catch (err) {
      console.log('Jurisdiction::handleBaseCurrencyChange', err);
    }
  };

  const handleToggleLastReporting = useCallback(
    (value, fundId, index) => {
      const mutatedForm = [...form];

      const selectedElForm = mutatedForm[index];
      const fundIndex = selectedElForm?.funds?.findIndex(fund => fund.key === fundId);

      selectedElForm.funds[fundIndex].isLastReporting = value;
      mutatedForm[index] = selectedElForm;

      setHasChanges(true);
      setForms(mutatedForm);
    },
    [form]
  );

  const handleChangeMasterFeederStatus = useCallback(
    (value, fundId) => {
      try {
        // find prev master id (if the fund we are changing was previously MASTER)
        let prevMasterFundId;
        for (const jurisdiction of form) {
          for (const fund of jurisdiction?.funds || []) {
            if (
              fund.masterFeederStatus === JURISDICTION_MASTER_STATUS_KEYS.MASTER &&
              fund.key === fundId
            ) {
              prevMasterFundId = fund.key;
              break;
            }
          }
          if (prevMasterFundId) break;
        }

        const shouldClearForTarget =
          !value ||
          value === JURISDICTION_MASTER_STATUS_KEYS.MASTER ||
          value === JURISDICTION_MASTER_STATUS_KEYS.NONE;

        const mutatedForm = form.map(jurisdiction => ({
          ...jurisdiction,
          funds: (jurisdiction?.funds || []).map(fund => {
            const originalIds = Array.isArray(fund.masterFundIds) ? fund.masterFundIds : [];

            // If this is the fund being updated and new status requires clearing its own masterFundIds
            if (fund.key === fundId && shouldClearForTarget) {
              return { ...fund, masterFundIds: [], masterFeederStatus: value };
            }

            // Otherwise, remove prevMasterFundId from every fund's masterFundIds (if exists)
            const filtered = prevMasterFundId
              ? originalIds.filter(id => id !== prevMasterFundId)
              : originalIds.slice();

            if (fund.key === fundId) {
              // update target fund's status but keep its (filtered) masterFundIds
              return { ...fund, masterFundIds: filtered, masterFeederStatus: value };
            }

            return { ...fund, masterFundIds: filtered };
          })
        }));

        setHasChanges(true);
        setForms(mutatedForm);
      } catch (err) {
        console.error('Jurisdiction::handleChangeMasterFeederStatus', err);
      }
    },
    [form]
  );

  const handleChangeMasterFundId = useCallback(
    (value, fundKey) => {
      try {
        const mutatedForm = form.map(jurisdiction => {
          let mutatedFunds = jurisdiction?.funds.map(fund => {
            if (fund?.key === fundKey) {
              return {
                ...fund,
                masterFundIds: value || []
              };
            }

            return fund;
          });

          return { ...jurisdiction, funds: mutatedFunds };
        });

        setHasChanges(true);
        setForms(mutatedForm);
      } catch (err) {
        console.log('Jurisdiction::handleChangeMasterFundId', err);
      }
    },
    [form]
  );

  const handleToggleNilReturn = useCallback(
    (value, fundId, index) => {
      const mutatedForm = [...form];

      const selectedElForm = mutatedForm[index];
      const fundIndex = selectedElForm?.funds?.findIndex(fund => fund.key === fundId);

      selectedElForm.funds[fundIndex].aifNoReportingFlag = value;
      mutatedForm[index] = selectedElForm;

      setHasChanges(true);
      setForms(mutatedForm);
    },
    [form]
  );

  const handleChangeCountry = useCallback(
    (value, index) => {
      const mutatedForm = [...form];
      const selectedElForm = mutatedForm[index];

      selectedElForm.country = value?.reporting_member_state ? value : null;
      mutatedForm[index] = selectedElForm;

      setHasChanges(true);
      setForms(mutatedForm);
    },
    [form]
  );

  const handleDeleteJurisdiction = useCallback(
    index => {
      const selectedJurisdiction = form[index];

      if (selectedJurisdiction.id) {
        setDeletingJurisdictionId(selectedJurisdiction.id);

        deleteJurisdiction.mutate(selectedJurisdiction.id, {
          onSuccess: () => {
            setForms(prev => prev.filter((_, idx) => idx !== index));
            setDeletingJurisdictionId(null);
            refetchJurisdictions();
          },
          onError: err => {
            toast.error(extractApiError(err, 'Unable to delete this jurisdiction.'));
            setDeletingJurisdictionId(null);
          }
        });
      } else {
        setForms(prev => prev.filter((_, idx) => idx !== index));
      }

      setHasChanges(true);
    },
    [form]
  );

  const validateAifmAndFunds = useCallback(
    form => {
      let hasError = false;

      form.forEach(aifm => {
        if (hasError) return;

        const countryCode = aifm.country?.reporting_member_state || aifm.country;
        const rules = countryRules[countryCode] || countryRules.Other;

        // AIFM CODE VALIDATION
        if (!rules.aifmRegex.test(aifm.aifm)) {
          toast.error(
            `Invalid ${REPORT_NAMINGS.PROJECT?.[reportType]} code for ${rules.country}, (e.g. ${rules.aifmExample})`,
            { duration: 8000 }
          );
          focusElementByID('input-' + aifm.generatedId);
          hasError = true;
          return;
        }

        // DUPLICATE FUND CODE CHECK
        const seenCodes = new Map();

        for (const fund of aifm.funds) {
          if (!fund?.isIncluded || fund?.isCustomMasterFund) continue;

          const code = fund.code?.trim();
          if (!code) continue;

          if (seenCodes.has(code)) {
            toast.error(
              `Duplicate ${REPORT_NAMINGS.FUND?.[reportType]} code "${code}" inside ${rules.country} - ${aifm.aifm}. Each fund must have a unique code.`,
              { duration: 8000 }
            );

            // focus second duplicate
            focusElementByID(`${aifm.generatedId}-${fund.key}`, true);
            hasError = true;
            return;
          }

          seenCodes.set(code, fund);
        }

        if (hasError) return;

        // FUND FORMAT VALIDATION
        for (const fund of aifm.funds) {
          if (hasError) return;
          if (!fund?.isIncluded || (fund?.isCustomMasterFund && !fund.code)) continue;

          if (!rules.fundRegex.test(fund.code)) {
            toast.error(
              `Invalid ${REPORT_NAMINGS.FUND?.[reportType]} code for ${rules.country} - ${fund.name}, (e.g. ${rules.aifExample})`,
              { duration: 8000 }
            );
            focusElementByID(`${aifm.generatedId}-${fund.key}`, true);
            hasError = true;
            return;
          }
        }
      });

      return hasError;
    },
    [form]
  );

  const handleSubmit = useCallback(
    e => {
      e.preventDefault();

      if (validateAifmAndFunds(form)) {
        // Stop if there are validation errors
        return;
      }

      if (disabledSubmitButton || isFeederHasNoMasterFund) {
        // Show a message or disable the button
        return;
      }

      // Proceed if there are no validation errors
      onSubmit(e, form);
    },
    [validateAifmAndFunds, form, onSubmit]
  );

  const onCreateCustomMasterFund = (fundKey, value) => {
    try {
      const mutatedForm = form.map(jurisdiction => {
        let mutatedFunds = jurisdiction?.funds.map(fund => {
          if (fund?.key === fundKey && value)
            return {
              ...fund,
              masterFundIds: [...fund.masterFundIds, value?.id]
            };

          return fund;
        });

        return { ...jurisdiction, funds: mutatedFunds };
      });

      setHasChanges(true);
      setForms(mutatedForm);
      refetchCustomMasterFunds();
    } catch (err) {
      console.log('Jurisdiction::onCreateCustomMasterFund', err);
    }
  };

  const onDeleteCustomMasterFund = fundKey => {
    try {
      const mutatedForm = form.map(jurisdiction => {
        let mutatedFunds = jurisdiction?.funds
          .filter(fund => fund.key !== fundKey)
          .map(fund => {
            // Remove deleted custom master fund from masterFundIds of other funds
            const filteredMasterFundIds = (fund.masterFundIds || []).filter(id => id !== fundKey);

            return { ...fund, masterFundIds: filteredMasterFundIds };
          });

        return { ...jurisdiction, funds: mutatedFunds };
      });

      setMutatedCustomMasterFunds(prev => prev.filter(el => el.id !== fundKey));
      setHasChanges(true);
      setForms(mutatedForm);
    } catch (err) {
      console.log('Jurisdiction::onDeleteCustomMasterFund', err);
    }
  };

  const focusElementByID = (id, withForceExpand = false) => {
    setForceExpand(withForceExpand);

    document.getElementById(id)?.focus();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  function openPopup(msg = 'You have unsaved data. Are you sure you want to proceed?') {
    return new Promise((resolve, reject) => {
      // Simulate opening popup
      setTimeout(() => {
        const userResponse = confirm(msg);

        if (userResponse) {
          setHasChanges(false);
        }

        resolve(userResponse);
      }, 100);
    });
  }

  const handleClickUniqueWarning = useCallback(() => {
    if (aifmUniqueIssues.length)
      return document
        .getElementById(`input-${aifmUniqueIssues[0]}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'end' });

    if (aifUniqueIssues.length) setForceExpand(true);

    if (aifUniqueIssues.length) {
      setTimeout(() => {
        document.getElementById(aifUniqueIssues[0])?.focus();
        document
          .getElementById(aifUniqueIssues[0])
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return;
    }
  }, [aifUniqueIssues, aifmUniqueIssues]);

  const handleClose = useCallback(async () => {
    if (hasChanges) {
      const userResponse = await openPopup();

      if (!userResponse) return;
    }

    onClose();
  }, [hasChanges]);

  useEffect(() => {
    agentStore.setCurrentSubScreen(WEB_PAGE_STATE_ENUMS.JurisdictionsDrawer);
    return () => agentStore.setCurrentSubScreen('');
  }, []);

  // Refs so the toolbar slot effects don't need to re-fire whenever the
  // submit/add closures change identity (every render via state).
  const handleSubmitRef = useRef();
  const handleAddJurisdictionRef = useRef();
  const handleClickUniqueWarningRef = useRef();
  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
    handleAddJurisdictionRef.current = handleAddJurisdiction;
    handleClickUniqueWarningRef.current = handleClickUniqueWarning;
  });

  // Save + Add Another Jurisdiction live in the page chrome's right-slot
  // toolbar. Save sits to the RIGHT of Add Another, so it occupies the
  // `primaryAction` slot (toolbar renders saveAction → exportAction →
  // primaryAction left-to-right). Skip registration when the user can't edit
  // so the toolbar doesn't show orphan buttons.
  useEffect(() => {
    if (!allowed) {
      setToolbarSlots(null);
      return undefined;
    }
    setToolbarSlots({
      saveAction: {
        label: 'Add Another Jurisdiction',
        onClick: () => handleAddJurisdictionRef.current?.(),
        variant: 'secondary',
        size: 'xs'
      },
      primaryAction: {
        label: `Save (${form.length})`,
        onClick: () => handleSubmitRef.current?.({ preventDefault: () => {} }),
        disabled: !!disableSubmitMessage || !!isMutating,
        loading: !!isMutating,
        variant: 'primary',
        size: 'xs'
      }
    });
    return () => setToolbarSlots(null);
  }, [allowed, form.length, disableSubmitMessage, isMutating, setToolbarSlots]);

  // Uniqueness warning lives in the toolbar's left slot. Render it as soon as
  // at least one jurisdiction is on the form so the user sees the constraint
  // before they save.
  useEffect(() => {
    if (form.length === 0) {
      setLabels({ toolbarLeftContent: null });
      return undefined;
    }
    setLabels({
      toolbarLeftContent: (
        <AIFMustBeUniqueWarning
          reportType={reportType}
          isAif={aifUniqueIssues.length}
          isAifm={aifmUniqueIssues.length}
          onClick={() => handleClickUniqueWarningRef.current?.()}
        />
      )
    });
    return () => setLabels({ toolbarLeftContent: null });
  }, [form.length, aifUniqueIssues.length, aifmUniqueIssues.length, reportType, setLabels]);

  return (
    <Box
      p="16px"
      h="100%"
      bg="gray.0"
      style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
    >
      <Box
        bg="white"
        p="16px"
        style={{
          borderRadius: 8,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0
        }}
      >
        <form
          onSubmit={handleSubmit}
          style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <ScrollArea.Autosize style={{ flex: 1, minHeight: 0 }} type="auto">
            {form.length > 0 && (
              <>
                <Text fz="16px" fw="600" lh="22px" mb="8px">
                  Regulators ({form.length})
                </Text>

                {form.map((_form, index) => {
                  const fundOptions = [...(mutatedCustomMasterFunds || []), ...(funds || [])];

                  return (
                    <ESMAJurisdiction
                      key={_form.generatedId}
                      funds={_form.funds}
                      fundsOptions={fundOptions}
                      country={_form.country}
                      aifmCode={_form.aifm}
                      reportType={reportType}
                      countries={countries}
                      generatedId={_form.generatedId}
                      forceExpand={forceExpand}
                      periodId={periodId}
                      workflowId={workflowId}
                      jurisdictionId={_form.id}
                      toggleIncluded={handleToggleIncluded}
                      toggleLastReporting={handleToggleLastReporting}
                      deleteDisabled={deleteJurisdiction.isLoading}
                      aifUniqueIssues={aifUniqueIssues}
                      aifmUniqueIssues={aifmUniqueIssues}
                      refetchCustomMasterFunds={refetchCustomMasterFunds}
                      closeForceExpand={() => setForceExpand(false)}
                      jurisdictionIndex={index}
                      handleChangeCountry={val => handleChangeCountry(val, index)}
                      handleChangeAIFCode={handleChangeAIFCode}
                      handleChangeAIFMCode={handleChangeAIFMCode}
                      handleBaseCurrencyChange={handleBaseCurrencyChange}
                      onCreateCustomMasterFund={onCreateCustomMasterFund}
                      deletingJurisdictionId={deletingJurisdictionId}
                      handleDeleteJurisdiction={() => handleDeleteJurisdiction(index)}
                      handleToggleNilReturn={handleToggleNilReturn}
                      handleChangeMasterFundId={handleChangeMasterFundId}
                      handleChangeMasterFeederStatus={handleChangeMasterFeederStatus}
                      onDeleteCustomMasterFund={onDeleteCustomMasterFund}
                      canManageWorkflow={canManageWorkflow}
                      isLockedPeriod={isLockedPeriod}
                    />
                  );
                })}
              </>
            )}

            {!form.length && (
              <NoJurisdictions
                isLoadingFunds={isLoadingFunds}
                onClick={handleAddJurisdiction}
                canManageWorkflow={canManageWorkflow}
                isLockedPeriod={isLockedPeriod}
              />
            )}
            <div id="jurisdiction-forms__end" />
          </ScrollArea.Autosize>
        </form>
      </Box>
    </Box>
  );
};
